import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { CarModel, FuelType, GearType } from '@services';
import { Divider, Grid, MenuItem, Stack } from '@mui/material';
import { FormikNumber, FormikTextfield } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';

type EntityType = CarModel;
type FormFields = Partial<CarModel>;

const getInitialValues = (entity?: Partial<EntityType>) => ({ ...entity });
const getGearTypeText = (inp: GearType): ReactNode => {
	if (inp === GearType.AUTOMATIC) {
		return <Tr.Portal path="car-models.gear-type-automatic" />;
	} else if (inp === GearType.MANUAL) {
		return <Tr.Portal path="car-models.gear-type-manual" />;
	} else if (inp === GearType.SEMI_AUTOMATIC) {
		return <Tr.Portal path="car-models.gear-type-semi-automatic" />;
	} else if (inp === GearType.SEQUENTIAL) {
		return <Tr.Portal path="car-models.gear-type-sequential" />;
	} else {
		return inp;
	}
};

export interface IAddEditCarModelFormProps {
	values?: Partial<EntityType>;
	onSubmit?: (values: Partial<EntityType>) => void;
}

export const AddEditCarModelForm: FC<IAddEditCarModelFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');

	const formik = useFormik<FormFields>({
		initialValues: getInitialValues(values),
		enableReinitialize: true,
		validateOnChange: true,
		validationSchema: yup.object({
			brand: yup.string().required(t('field-required')),
			model: yup.string().required(t('field-required')),
			motorFuelType: yup.string().required(t('field-required')),
			version: yup.string().required(t('field-required')),
			body: yup.string().required(t('field-required')),
			carSegment: yup.string().required(t('field-required')),
			gearType: yup.string().optional().nullable(),
			seats: yup.number().optional().nullable(),
			doors: yup.number().optional().nullable(),
			maxSpeed: yup.number().optional().nullable(),
			euroMotor: yup.string().optional().nullable(),
			wheelDrive: yup.string().optional().nullable(),
			codice_eurotax: yup.number().optional().nullable(),
			id_dbmotori: yup.string().optional().nullable(),
			wltpNedc: yup.string().optional().nullable(),

			price: yup.number().required(t('field-required')),
			leasingPrice: yup.number().optional().nullable(),

			co2EmissionUrban: yup.number().optional().nullable(),
			co2EmissionHighway: yup.number().optional().nullable(),
			co2EmissionCombined: yup.number().optional().nullable(),
			fuelConsumptionUrban: yup.number().optional().nullable(),
			fuelConsumptionHighway: yup.number().optional().nullable(),
			fuelConsumptionCombined: yup.number().optional().nullable(),

			wltpConsumptionUrbanCold: yup.number().optional().nullable(),
			wltpConsumptionHighwayCold: yup.number().optional().nullable(),
			wltpConsumptionCombinedCold: yup.number().optional().nullable(),
			wltpConsumptionUrbanMild: yup.number().optional().nullable(),
			wltpConsumptionHighwayMild: yup.number().optional().nullable(),
			wltpConsumptionCombinedMild: yup.number().optional().nullable(),
			realConsumptionUrbanCold: yup.number().optional().nullable(),
			realConsumptionHighwayCold: yup.number().optional().nullable(),
			realConsumptionCombinedCold: yup.number().optional().nullable(),
			realConsumptionUrbanMild: yup.number().optional().nullable(),
			realConsumptionHighwayMild: yup.number().optional().nullable(),
			realConsumptionCombinedMild: yup.number().optional().nullable(),

			wltpRangeUrbanCold: yup.number().optional().nullable(),
			wltpRangeHighwayCold: yup.number().optional().nullable(),
			wltpRangeCombinedCold: yup.number().optional().nullable(),
			wltpRangeUrbanMild: yup.number().optional().nullable(),
			wltpRangeHighwayMild: yup.number().optional().nullable(),
			wltpRangeCombinedMild: yup.number().optional().nullable(),
			realRangeUrbanCold: yup.number().optional().nullable(),
			realRangeHighwayCold: yup.number().optional().nullable(),
			realRangeCombinedCold: yup.number().optional().nullable(),
			realRangeUrbanMild: yup.number().optional().nullable(),
			realRangeHighwayMild: yup.number().optional().nullable(),
			realRangeCombinedMild: yup.number().optional().nullable(),

			electricalMotorEfficiency: yup.number().optional().nullable(),
			batteryCapacity: yup.number().optional().nullable(),
			batteryUsable: yup.number().optional().nullable(),
			batterySlowPortType: yup.string().optional().nullable(),
			batterySlowChargePower: yup.number().optional().nullable(),
			batterySlowChargeSpeed: yup.number().optional().nullable(),
			batteryFastPortType: yup.string().optional().nullable(),
			batteryFastChargePower: yup.number().optional().nullable(),
			batteryFastChargeSpeed: yup.number().optional().nullable()
		}),
		onSubmit: (submittingValues, api) => {
			onSubmit?.(submittingValues);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: (
						<Tr.Portal path={values ? 'car-models.add-edit-form.update-button' : 'car-models.add-edit-form.add-button'} />
					),
					onClick: submitForm
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion title={<Tr.Portal path="car-models.basic-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="brand"
									title={t('car-models.brand')}
									placeholder={t('car-models.brand')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="model"
									title={t('car-models.model')}
									placeholder={t('car-models.model')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="motorFuelType"
									title={t('car-models.fuel')}
									placeholder={t('car-models.fuel')}
									fullWidth
									required
									select
								>
									{Object.values(FuelType).map((type) => (
										<MenuItem key={`fuel-type-${type}`} value={type}>
											{type}
											{/* {capitalCase(type).toUpperCase()} */}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>

							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="version"
									title={t('car-models.version')}
									placeholder={t('car-models.version')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="body"
									title={t('car-models.body')}
									placeholder={t('car-models.body')}
									fullWidth
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="carSegment"
									title={t('car-models.car-segment')}
									placeholder={t('car-models.car-segment')}
									fullWidth
									required
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="gearType"
									title={t('car-models.gear-type')}
									placeholder={t('car-models.gear-type')}
									fullWidth
									select
								>
									{Object.values(GearType).map((type) => (
										<MenuItem key={`gear-type-${type}`} value={type}>
											{getGearTypeText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="seats"
									title={t('car-models.seats')}
									placeholder={t('car-models.seats')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="doors"
									title={t('car-models.doors')}
									placeholder={t('car-models.doors')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="maxSpeed"
									title={t('car-models.max-speed')}
									placeholder={t('car-models.max-speed')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="euroMotor"
									title={t('car-models.euro-motor')}
									placeholder={t('car-models.euro-motor')}
									fullWidth
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="wheelDrive"
									title={t('car-models.wheel-drive')}
									placeholder={t('car-models.wheel-drive')}
									fullWidth
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="codice_eurotax"
									title={t('car-models.euro-tax-code')}
									placeholder={t('car-models.euro-tax-code')}
									fullWidth
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="id_dbmotori"
									title={t('car-models.engine-db-id')}
									placeholder={t('car-models.engine-db-id')}
									fullWidth
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="wltpNedc"
									title={t('car-models.wltp-nedc')}
									placeholder={t('car-models.wltp-nedc')}
									fullWidth
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>

					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="car-models.pricing-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="price"
									title={t('car-models.price')}
									placeholder={t('car-models.price')}
									fullWidth
									required
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="leasingPrice"
									title={t('car-models.leasing-price')}
									placeholder={t('car-models.leasing-price')}
									fullWidth
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>

					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="car-models.emission-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="co2EmissionUrban"
									title={t('car-models.co2-emission-urban')}
									placeholder={t('car-models.co2-emission-urban')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="co2EmissionHighway"
									title={t('car-models.co2-emission-highway')}
									placeholder={t('car-models.co2-emission-highway')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="co2EmissionCombined"
									title={t('car-models.co2-emission-combined')}
									placeholder={t('car-models.co2-emission-combined')}
									fullWidth
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>

					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="car-models.consumption-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="fuelConsumptionUrban"
									title={t('car-models.fuel-consumption-urban')}
									placeholder={t('car-models.fuel-consumption-urban')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="fuelConsumptionHighway"
									title={t('car-models.fuel-consumption-highway')}
									placeholder={t('car-models.fuel-consumption-highway')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="fuelConsumptionCombined"
									title={t('car-models.fuel-consumption-combined')}
									placeholder={t('car-models.fuel-consumption-combined')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionUrbanCold"
									title={t('car-models.wltp-consumption-urban-cold')}
									placeholder={t('car-models.wltp-consumption-urban-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionHighwayCold"
									title={t('car-models.wltp-consumption-highway-cold')}
									placeholder={t('car-models.wltp-consumption-highway-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionCombinedCold"
									title={t('car-models.wltp-consumption-combined-cold')}
									placeholder={t('car-models.wltp-consumption-combined-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionUrbanMild"
									title={t('car-models.wltp-consumption-urban-mild')}
									placeholder={t('car-models.wltp-consumption-urban-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionHighwayMild"
									title={t('car-models.wltp-consumption-highway-mild')}
									placeholder={t('car-models.wltp-consumption-highway-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpConsumptionCombinedMild"
									title={t('car-models.wltp-consumption-combined-mild')}
									placeholder={t('car-models.wltp-consumption-combined-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionUrbanCold"
									title={t('car-models.real-consumption-urban-cold')}
									placeholder={t('car-models.real-consumption-urban-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionHighwayCold"
									title={t('car-models.real-consumption-highway-cold')}
									placeholder={t('car-models.real-consumption-highway-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionCombinedCold"
									title={t('car-models.real-consumption-combined-cold')}
									placeholder={t('car-models.real-consumption-combined-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionUrbanMild"
									title={t('car-models.real-consumption-urban-mild')}
									placeholder={t('car-models.real-consumption-urban-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionHighwayMild"
									title={t('car-models.real-consumption-highway-mild')}
									placeholder={t('car-models.real-consumption-highway-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realConsumptionCombinedMild"
									title={t('car-models.real-consumption-combined-mild')}
									placeholder={t('car-models.real-consumption-combined-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>

					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="car-models.range-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeUrbanCold"
									title={t('car-models.wltp-range-urban-cold')}
									placeholder={t('car-models.wltp-range-urban-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeHighwayCold"
									title={t('car-models.wltp-range-highway-cold')}
									placeholder={t('car-models.wltp-range-highway-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeCombinedCold"
									title={t('car-models.wltp-range-combined-cold')}
									placeholder={t('car-models.wltp-range-combined-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeUrbanMild"
									title={t('car-models.wltp-range-urban-mild')}
									placeholder={t('car-models.wltp-range-urban-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeHighwayMild"
									title={t('car-models.wltp-range-highway-mild')}
									placeholder={t('car-models.wltp-range-highway-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="wltpRangeCombinedMild"
									title={t('car-models.wltp-range-combined-mild')}
									placeholder={t('car-models.wltp-range-combined-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeUrbanCold"
									title={t('car-models.real-range-urban-cold')}
									placeholder={t('car-models.real-range-urban-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeHighwayCold"
									title={t('car-models.real-range-highway-cold')}
									placeholder={t('car-models.real-range-highway-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeCombinedCold"
									title={t('car-models.real-range-combined-cold')}
									placeholder={t('car-models.real-range-combined-cold')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeUrbanMild"
									title={t('car-models.real-range-urban-mild')}
									placeholder={t('car-models.real-range-urban-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeHighwayMild"
									title={t('car-models.real-range-highway-mild')}
									placeholder={t('car-models.real-range-highway-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="realRangeCombinedMild"
									title={t('car-models.real-range-combined-mild')}
									placeholder={t('car-models.real-range-combined-mild')}
									fullWidth
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>

					<Divider light />

					<FormStepQuestion title={<Tr.Portal path="car-models.battery-information" />}>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="electricalMotorEfficiency"
									title={t('car-models.electrical-motor-efficiency')}
									placeholder={t('car-models.electrical-motor-efficiency')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batteryCapacity"
									title={t('car-models.battery-capacity')}
									placeholder={t('car-models.battery-capacity')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batteryUsable"
									title={t('car-models.battery-usable')}
									placeholder={t('car-models.battery-usable')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="batterySlowPortType"
									title={t('car-models.battery-slow-port-type')}
									placeholder={t('car-models.battery-slow-port-type')}
									fullWidth
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batterySlowChargePower"
									title={t('car-models.battery-slow-charge-power')}
									placeholder={t('car-models.battery-slow-charge-power')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batterySlowChargeSpeed"
									title={t('car-models.battery-slow-charge-speed')}
									placeholder={t('car-models.battery-slow-charge-speed')}
									fullWidth
									decimal={0}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="batteryFastPortType"
									title={t('car-models.battery-fast-port-type')}
									placeholder={t('car-models.battery-fast-port-type')}
									fullWidth
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batteryFastChargePower"
									title={t('car-models.battery-fast-charge-power')}
									placeholder={t('car-models.battery-fast-charge-power')}
									fullWidth
									decimal={0}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="batteryFastChargeSpeed"
									title={t('car-models.battery-fast-charge-speed')}
									placeholder={t('car-models.battery-fast-charge-speed')}
									fullWidth
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
